import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import { Result, ToResult, pipeDataResult, toDataResult } from '@team23/ts-result';
import { FilterState, limitFilterValues$ } from '@ui/shared/domain-filter';
import { FilterDataStatus, FilterLimitingConfig, FilterOption } from '@ui/shared/util-filter';
import { BehaviorSubject, Observable, combineLatest, distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs';
import { BaseFilterComponent } from '../base-filter/base-filter.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ui-base-filter',
    template: '',
    styleUrls: [],
    standalone: true,
})
export abstract class BaseFilterWithOptionsComponent extends BaseFilterComponent {
    @Output()
    public readonly reloadFilterOptions = new EventEmitter<void>();

    public readonly limitedValues$: Observable<Array<FilterOption>>;
    public readonly limitedOptions$: Observable<Result<Array<FilterOption>, FilterDataStatus>>;
    public readonly areOptionsReduced$: Observable<boolean>;

    private readonly _options$ = new BehaviorSubject<Result<Array<FilterOption>, FilterDataStatus>>(ToResult.fromData([]));
    private readonly _limitingConfig$ = new BehaviorSubject<FilterLimitingConfig | undefined>(undefined);

    constructor(
        activeFilterService: FilterState,
    ) {
        super(activeFilterService);

        // Apply the limiting config to the options from the input.
        this.limitedOptions$
            = this._limitingConfig$
                .pipe(
                    switchMap(filterLimitingConfig =>
                        (filterLimitingConfig
                            ? this._options$
                                .pipe(
                                    pipeDataResult(
                                        map(options => options.data),
                                        limitFilterValues$(filterLimitingConfig, activeFilterService),
                                        toDataResult(),
                                    ),
                                )
                            : this._options$),
                    ),
                    distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
                    shareReplay(1),
                );

        // Determine whether the limited options are fewer than the unlimited options.
        this.areOptionsReduced$ = combineLatest([
            this._options$,
            this.limitedOptions$,
        ])
            .pipe(
                map(([options, limitedOptions]) => {
                    const optionsAmount = options.data?.length ?? 0;
                    const limitedOptionsAmount = limitedOptions.data?.length ?? 0;

                    return optionsAmount > limitedOptionsAmount;
                }),
            );

        // Get the values and ensure that invalid values are removed.
        this.limitedValues$ = combineLatest([
            this.values$,
            this.limitedOptions$,
        ])
            .pipe(
                // Whenever the options or values change, ensure that all values are removed that aren't available anymore.
                map(([currentValues, limitedOptions]) => {
                    if (currentValues.some(currentValue => !limitedOptions.data?.includes(currentValue))) {
                        const limitedValues = currentValues
                            .filter(currentValue =>
                                limitedOptions.data?.includes(currentValue),
                            );

                        this.onValueChange(limitedValues);

                        return limitedValues;
                    }

                    return currentValues;
                }),
            );
    }

    @Input({ required: true })
    public set options(value: Result<Array<FilterOption>, FilterDataStatus>) {
        this._options$.next(value);
    }

    @Input()
    public set limitingConfig(value: FilterLimitingConfig | undefined) {
        this._limitingConfig$.next(value);
    }
}
